import { HttpStatusCode } from "axios";
import { LocationServiceApi } from "../serverConnectors";
import { CityAdminDto, Country, Location, Region } from "../types";

export interface LocationServiceInterface {
  resolveIpToCountry(ipAddress: string): Promise<Country | null>;
  detectCountry(): Promise<Country | null>;
  resolveCountryByCoordinates(
    latitude: number,
    longitude: number,
  ): Promise<Country | null>;
  findCountryByCode(code: string): Promise<Country | null>;
  findCountries(status?: number): Promise<Country[]>;
  findCitiesByCountryName(countryName: string): Promise<Location[]>;
  createCountry(country: Country): Promise<Country>;
  updateCountry(country: Country): Promise<Country>;
  deleteCountry(countryId: number): Promise<void>;

  findRegionsByCountry(countryName: string): Promise<Region[]>;
  createRegion(region: Region): Promise<Region>;
  updateRegion(region: Region): Promise<Region>;
  deleteRegion(regionId: number): Promise<void>;

  findCitiesByRegionId(regionId: number): Promise<CityAdminDto[]>;
  updateCity(city: CityAdminDto): Promise<CityAdminDto>;
  createCity(city: CityAdminDto): Promise<CityAdminDto>;
  deleteCity(cityId: number): Promise<void>;
}

export class LocationService
  extends LocationServiceApi
  implements LocationServiceInterface
{
  private static readonly REQUEST_TIMEOUT_MS = 5000;

  async resolveIpToCountry(ipAddress: string): Promise<Country | null> {
    let country: Country | null = null;
    try {
      const response = await this.axios.get(
        `/ip?ip=${encodeURIComponent(ipAddress)}`,
        { timeout: 3000 },
      );
      if (response.status === HttpStatusCode.Ok) {
        country = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::resolveIpToCountry", error);
    }
    return country;
  }

  async findCountryByCode(code: string): Promise<Country | null> {
    let country: Country | null = null;
    try {
      const response = await this.axios.get<Country>(
        `/country/code/${encodeURIComponent(code)}`,
        { timeout: 3000 },
      );
      if (response.status === HttpStatusCode.Ok) {
        country = response.data;
      }
    } catch (error: unknown) {
      console.error("LocationService::findCountryByCode", error);
    }
    return country;
  }

  async detectCountry(): Promise<Country | null> {
    let country: Country | null = null;
    try {
      const response = await this.axios.get<Country>("/country/detect", {
        timeout: 3000,
      });
      if (response.status === HttpStatusCode.Ok) {
        country = response.data;
      }
    } catch (error: unknown) {
      console.error("LocationService::detectCountry", error);
    }
    return country;
  }

  async resolveCountryByCoordinates(
    latitude: number,
    longitude: number,
  ): Promise<Country | null> {
    let country: Country | null = null;
    try {
      const roundedLatitude = Number(latitude.toFixed(2));
      const roundedLongitude = Number(longitude.toFixed(2));
      const response = await this.axios.post<Country>(
        "/country/coordinates",
        {
          lat: roundedLatitude,
          lng: roundedLongitude,
        },
        { timeout: 3000 },
      );
      if (response.status === HttpStatusCode.Ok) {
        country = response.data;
      }
    } catch (error: unknown) {
      console.error("LocationService::resolveCountryByCoordinates", error);
    }
    return country;
  }

  /**
   *
   * @param status 1 for active countries, 0 for inactive countries, and null for all countries.
   * @returns
   */
  async findCountries(status?: number): Promise<Country[]> {
    let countries: Country[] = [];
    try {
      const url = `/country${status === undefined ? "" : `?status=${status}`}`;
      const response = await this.axios.get(url, {
        timeout: LocationService.REQUEST_TIMEOUT_MS,
      });
      if (response.status === HttpStatusCode.Ok) {
        countries = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::findCountries", error);
    }
    return countries;
  }

  async findCitiesByCountryName(countryName: string): Promise<Location[]> {
    let locations: Location[] = [];
    try {
      const response = await this.axios.get("/city/all", {
        params: { country: countryName },
      });
      if (response.status === HttpStatusCode.Ok) {
        locations = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::findCitiesByCountryName", error);
    }
    return locations;
  }

  async createCountry(country: Country): Promise<Country> {
    let createdCountry: Country | null = null;
    try {
      const response = await this.axios.post("/country", country);
      if (response.status === HttpStatusCode.Created) {
        createdCountry = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::createCountry", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to create country. Please try again later.",
      );
    }
    return createdCountry || country;
  }

  async updateCountry(country: Country): Promise<Country> {
    let updatedCountry: Country | null = null;
    let response: any;
    try {
      response = await this.axios.put(`/country`, country);
      if (response.status === HttpStatusCode.Ok) {
        updatedCountry = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::updateCountry", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to update country. Please try again later.",
      );
    }
    return updatedCountry || country;
  }

  async deleteCountry(countryId: number): Promise<void> {
    try {
      const response = await this.axios.delete(`/country/${countryId}`);
      if (response.status !== HttpStatusCode.NoContent) {
        throw new Error("Failed to delete country. Please try again later.");
      }
    } catch (error: any) {
      console.error("LocationService::deleteCountry", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to delete country. Please try again later.",
      );
    }
  }

  async findRegionsByCountry(countryName: string): Promise<Region[]> {
    let regions: Region[] = [];
    try {
      const response = await this.axios.get(
        `/region?country=${encodeURIComponent(countryName)}`,
      );
      if (response.status === HttpStatusCode.Ok) {
        regions = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::findRegionsByCountry", error);
    }
    return regions;
  }

  async createRegion(region: Region): Promise<Region> {
    let createdRegion: Region | null = null;
    try {
      const response = await this.axios.post("/region", region);
      if (response.status === HttpStatusCode.Created) {
        createdRegion = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::createRegion", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to create region. Please try again later.",
      );
    }
    return createdRegion || region;
  }

  async updateRegion(region: Region): Promise<Region> {
    let updatedRegion: Region | null = null;
    try {
      const response = await this.axios.put(`/region`, region);
      if (response.status === HttpStatusCode.Ok) {
        updatedRegion = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::updateRegion", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to update region. Please try again later.",
      );
    }
    return updatedRegion || region;
  }

  async deleteRegion(regionId: number): Promise<void> {
    try {
      const response = await this.axios.delete(`/region/${regionId}`);
      if (response.status !== HttpStatusCode.NoContent) {
        throw new Error("Failed to delete region. Please try again later.");
      }
    } catch (error: any) {
      console.error("LocationService::deleteRegion", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to delete region. Please try again later.",
      );
    }
  }

  async findCitiesByRegionId(regionId: number): Promise<CityAdminDto[]> {
    let cities: CityAdminDto[] = [];
    try {
      const response = await this.axios.get(`/city/region/${regionId}`);
      if (response.status === HttpStatusCode.Ok) {
        cities = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::findCitiesByRegionId", error);
    }
    return cities;
  }

  async updateCity(city: CityAdminDto): Promise<CityAdminDto> {
    let updatedCity: CityAdminDto | null = null;
    try {
      const response = await this.axios.put(`/city`, city);
      if (response.status === HttpStatusCode.Ok) {
        updatedCity = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::updateCity", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to update city. Please try again later.",
      );
    }
    return updatedCity || city;
  }

  async createCity(city: CityAdminDto): Promise<CityAdminDto> {
    let createdCity: CityAdminDto | null = null;
    try {
      const response = await this.axios.post("/city", city);
      if (response.status === HttpStatusCode.Created) {
        createdCity = response.data;
      }
    } catch (error: any) {
      console.error("LocationService::createCity", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to create city. Please try again later.",
      );
    }
    return createdCity || city;
  }

  async deleteCity(cityId: number): Promise<void> {
    try {
      const response = await this.axios.delete(`/city/${cityId}`);
      if (response.status !== HttpStatusCode.NoContent) {
        throw new Error("Failed to delete city. Please try again later.");
      }
    } catch (error: any) {
      console.error("LocationService::deleteCity", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to delete city. Please try again later.",
      );
    }
  }
}
